import React, { useEffect, useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { getErrorMessage } from "utils/errorHelpers";
import { PricingVector } from "client/api-v2";
import type { RateCard, SubscriptionRateCardAssignment } from "client/api-v2";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { useRbacContext } from "components/authorization/RbacProvider";
import { createRateCard, draftRateCardAssignment } from "../services/rateCardService";
import { RateCardTermsFields } from "./RateCardTermsFields";
import type { CreateRateCardFormValues } from "./createRateCardForm";
import { collectErrorMessages, toRateCardTerms } from "./createRateCardForm";

/**
 * The entry point decides what saving means: the Rate Cards list page creates a
 * standalone shared card, while a subscription's rate card page creates a custom card
 * and stages it against that subscription as a draft assignment (activation stays an
 * explicit action on the details page).
 */
export type CreateRateCardMode = { kind: "shared" } | { kind: "subscription"; subscriptionId: string };

interface CreateRateCardDialogProps {
  open: boolean;
  mode: CreateRateCardMode;
  onCancel: () => void;
  /** In subscription mode the draft assignment staged alongside the card is passed too. */
  onCreated: (rateCard: RateCard, assignment?: SubscriptionRateCardAssignment) => void;
}

const formId = "create-rate-card-form";

const defaultValues: CreateRateCardFormValues = {
  label: "",
  // Project and Machine are the vectors every card prices in practice (the public
  // default card prices exactly these two), so both rows are present up front.
  unitPrices: [
    { vector: PricingVector.Project, listPricePerUnit: "" },
    { vector: PricingVector.Machine, listPricePerUnit: "" },
  ],
  discountExponent: "0",
  discountThresholdUnits: "0",
  overallDiscountPercent: "0",
  finalPriceRoundDownToNearest: "",
  fixedFees: [],
  reason: "",
};

export function CreateRateCardDialog({ open, mode, onCancel, onCreated }: CreateRateCardDialogProps) {
  const [serverError, setServerError] = useState<string | undefined>();
  const { isAuthorized } = useRbacContext();
  const canSave = isAuthorized(RbacPolicies.RateCards.ReadWrite);

  const methods = useForm<CreateRateCardFormValues>({ defaultValues });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = methods;

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setServerError(undefined);
    }
  }, [open, reset]);

  const onSubmit = async (values: CreateRateCardFormValues) => {
    setServerError(undefined);
    try {
      const terms = toRateCardTerms(values);
      if (mode.kind === "shared") {
        onCreated(await createRateCard(terms));
      } else {
        const response = await draftRateCardAssignment(mode.subscriptionId, terms, values.reason.trim());
        onCreated(response.rateCard, response.assignment);
      }
    } catch (e) {
      setServerError(getErrorMessage(e));
    }
  };

  const errorMessages = [...(serverError ? [serverError] : []), ...collectErrorMessages(errors)];

  return (
    // Escape/backdrop dismissal is ignored mid-save: the request would still complete,
    // just invisibly, with the caller never told the card was created.
    <Dialog open={open} onClose={() => !isSubmitting && onCancel()} maxWidth="md" fullWidth>
      <DialogTitle>Create rate card</DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* DialogContent that follows a DialogTitle has no top padding, which clips
                the first row's outlined-input labels without this. */}
            <Grid container spacing={3} sx={{ pt: 1 }}>
              {errorMessages.length > 0 && (
                <Grid size={12}>
                  <Alert severity="error">
                    {errorMessages.map((message) => (
                      <div key={message}>{message}</div>
                    ))}
                  </Alert>
                </Grid>
              )}

              <RateCardTermsFields />

              {mode.kind === "subscription" && (
                <Grid size={12}>
                  <Controller
                    name="reason"
                    control={control}
                    // The trimmed value is what gets sent, so the limit applies to it.
                    rules={{
                      validate: (value) => {
                        const trimmed = value.trim();
                        if (trimmed === "") {
                          return "Reason is required";
                        }
                        return trimmed.length <= 500 || "Reason must be 500 characters or fewer";
                      },
                    }}
                    render={({ field: { ref, ...rest }, fieldState }) => (
                      <TextField
                        {...rest}
                        inputRef={ref}
                        required
                        fullWidth
                        multiline
                        rows={3}
                        label="Reason"
                        error={fieldState.invalid}
                        helperText={
                          fieldState.error?.message ?? "Stored in the audit trail with who made the change and when"
                        }
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" form={formId} variant="contained" color="primary" disabled={isSubmitting || !canSave}>
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
